/* eslint-disable @typescript-eslint/no-explicit-any */
import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef,  } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Edit, Receipt } from '@mui/icons-material';
import { Chip, Tooltip } from '@mui/material';
import { Consumer } from '@/api/bol-notifications';
import { renderChip } from '@/utils/datagridFormatters';

interface ConsumerColumnsProps {
  onEdit: (consumer: Consumer) => void;
  onDelete: (consumer: Consumer) => void;
  onViewProofOfDelivery?: (consumer: Consumer) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  canViewProofOfDelivery?: boolean;
  editTooltip?: string;
  deleteTooltip?: string;
  proofOfDeliveryTooltip?: string;
}

/**
 * Format webhook headers as "name: value"
 */
const formatWebhookHeaders = (
  headers: Array<{ name: string; value: string }> | undefined,
  delimiter: string = ', '
): string => {
  if (!headers || !Array.isArray(headers) || headers.length === 0) return '-';
  const formatted = headers
    .filter(h => h.name && h.value)
    .map(h => `${h.name}: ${h.value}`);
  return formatted.length > 0 ? formatted.join(delimiter) : '-';
};

/**
 * Format webhook query parameters as "name: value"
 */
const formatWebhookQueryParameters = (
  queryParams: Array<{ name: string; value: string }> | undefined,
  delimiter: string = ', '
): string => {
  if (!queryParams || !Array.isArray(queryParams) || queryParams.length === 0) return '-';
  const formatted = queryParams
    .filter(q => q.name && q.value)
    .map(q => `${q.name}: ${q.value}`);
  return formatted.length > 0 ? formatted.join(delimiter) : '-';
};

// Default visibility for existing columns (visible by default)
export const defaultConsumerColumnVisibilityModel: Record<string, boolean> = {
  // Original visible columns
  name: true,
  protocol: true,
  status: true,
  webhook: true,
  batch: true,
  createdAt: true,
  actions: true,
  // New columns (hidden by default)
  id: false,
  disabledAt: false,
  updatedAt: false,
  'webhook.method': false,
  'webhook.headers': false,
  'webhook.queryParameters': false,
};

export const createConsumerColumns = ({ 
  onEdit, 
  onDelete, 
  onViewProofOfDelivery,
  canEdit = true, 
  canDelete = true, 
  canViewProofOfDelivery = true,
  editTooltip = '', 
  deleteTooltip = '',
  proofOfDeliveryTooltip = ''
}: ConsumerColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    width: 250,
    flex: 1,
  },
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 200,
  },
  {
    field: 'protocol',
    headerName: 'Protocol',
    width: 120,
    renderCell: (params: any) => (
      <Chip label={params.value} size="small" color="primary" />
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 100,
    renderCell: (params: any) => renderChip('consumerStatus', params.value),
  },
  {
    field: 'webhook',
    headerName: 'Webhook URL',
    flex: 2,
    width: 250,
    valueGetter: (_value: any, row: any) => row.webhook?.url || '',
  },
  {
    field: 'batch',
    headerName: 'Batch Config',
    width: 120,
    valueGetter: (_value: any, row: any) => `${row.batch?.capacity || 10}/${row.batch?.minInterval || 1000}ms`,
  },
  {
    field: 'disabledAt',
    headerName: 'Disabled At',
    width: 150,
    valueFormatter: (value: string | number | Date | null | undefined) => value ? new Date(value).toLocaleString() : '-',
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    width: 150,
    valueFormatter: (value: any) => new Date(value).toLocaleDateString(),
  },
  // New columns (hidden by default)
  {
    field: 'updatedAt',
    headerName: 'Updated',
    width: 150,
    valueFormatter: (value: any) => new Date(value).toLocaleDateString(),
  },
  {
    field: 'webhook.method',
    headerName: 'HTTP Method',
    width: 120,
    valueGetter: (_value: any, row: any) => row.webhook?.method || 'POST',
  },
  {
    field: 'webhook.headers',
    headerName: 'Webhook Headers',
    width: 200,
    flex: 1,
    valueGetter: (_value: any, row: any) => formatWebhookHeaders(row.webhook?.headers),
  },
  {
    field: 'webhook.queryParameters',
    headerName: 'Query Parameters',
    width: 200,
    flex: 1,
    valueGetter: (_value: any, row: any) => formatWebhookQueryParameters(row.webhook?._queryParameters),
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 180,
    hideable: false, // Always visible
    getActions: (params: any) => {
      const actions = [
        <Tooltip title={!canEdit ? editTooltip : ''} key="edit-tooltip">
          <span>
            <GridActionsCellItem
              icon={<Edit />}
              label="Edit"
              onClick={() => onEdit(params.row)}
              disabled={!canEdit}
              color="primary"
            />
          </span>
        </Tooltip>,
        <Tooltip title={!canDelete ? deleteTooltip : ''} key="delete-tooltip">
          <span>
            <GridActionsCellItem
              icon={<Delete />}
              label="Delete"
              onClick={() => onDelete(params.row)}
              disabled={!canDelete}
              color="error"
            />
          </span>
        </Tooltip>,
      ];

      // Add proof of delivery action if callback is provided
      if (onViewProofOfDelivery) {
        actions.push(
          <Tooltip title={!canViewProofOfDelivery ? proofOfDeliveryTooltip : 'View Proofs of Delivery'} key="proof-tooltip">
            <span>
              <GridActionsCellItem
                icon={<Receipt />}
                label="Proofs of Delivery"
                onClick={() => onViewProofOfDelivery(params.row)}
                disabled={!canViewProofOfDelivery}
                color="info"
              />
            </span>
          </Tooltip>
        );
      }

      return actions;
    },
  },
];